import React, { useEffect, useState } from "react";
import "./reviewModal.scss";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import Loader from "../Loader/Loader";

const hasDetailsChanged = (initialDetails, currentDetails) => {
  return (
    initialDetails?.score !== currentDetails?.score || currentDetails?.comment
  );
};

// Helper function to validate score
const validateScore = (score, maxExerciseScore) => {
  if (score === null || score === undefined) {
    return "Score cannot be empty";
  }
  if (score > maxExerciseScore) {
    return "Score cannot be higher than Max Score";
  }
  return "";
};

// Helper function to get dialog title
const getDialogTitle = (isEditMode, isReopenTextField) => {
  if (isEditMode) return "Edit Exercise";
  if (isReopenTextField) return "Re-Open Exercise";
  return "Review Exercise";
};

// Helper function to handle score input change
const handleScoreInputChange = (value, name, setScoreError, handleInputReviewInput) => {
  setScoreError(false);
  handleInputReviewInput(value, name);
};

// Helper function to handle comment input change
const handleCommentInputChange = (
  value,
  name,
  setCommentError,
  setCommentLengthError,
  handleInputReviewInput
) => {
  setCommentError(false);
  if (value?.trim().length <= 500) {
    setCommentLengthError(false);
    handleInputReviewInput(value, name);
  } else {
    setCommentLengthError(true);
  }
};

const ReviewModal = ({
  open,
  handleClose,
  initialExerciseDetails,
  handleInputReviewInput,
  handleSubmitReview,
  reviewDetails,
  isEditMode,
  isReopenTextField,
  isReviewMode,
}) => {
  const isLoading = useSelector((state) => state?.commentsList?.loading);
  const [loader, setLoader] = useState(false);
  const [scoreError, setScoreError] = useState("");
  const [commentError, setCommentError] = useState(false);
  const [commentLengthError, setCommentLengthError] = useState(false);

  const [submitDetails, setSubmitDetails] = useState({
    json: {
      status: "IN_REVIEW",
      score: null,
      comment: "",
    },
  });

  const exerciseDetailsChanged = hasDetailsChanged(
    initialExerciseDetails,
    reviewDetails
  );

  const onClose = () => {
    setScoreError(false);
    setCommentError(false);
    setCommentLengthError(false);
    handleClose();
  };

  const onSubmit = async () => {
    let hasScoreError = false;
    let hasCommentError = false;

    if (!isReopenTextField) {
      const scoreErr = validateScore(
        reviewDetails?.score,
        reviewDetails?.maxExerciseScore
      );
      setScoreError(scoreErr);
      hasScoreError = !!scoreErr;
    }

    hasCommentError = !reviewDetails?.comment;
    setCommentError(hasCommentError);

    if (!hasScoreError && !hasCommentError) {
      setLoader(true);
      const status = isReopenTextField ? "REOPENED" : "REVIEWED";
      const score = !isReopenTextField ? +reviewDetails?.score : null;
      
      setSubmitDetails({
        ...submitDetails,
        json: {
          ...submitDetails.json,
          status,
          score,
          comment: reviewDetails?.comment,
        },
      });
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!scoreError && !commentError) {
      handleSubmitReview(submitDetails);
      handleClose();
      setScoreError(false);
      setCommentError(false);
    }
  }, [submitDetails]);

  const handleInputChange = (value, name) => {
    if (name === "score") {
      handleScoreInputChange(value, name, setScoreError, handleInputReviewInput);
    } else if (name === "comment") {
      handleCommentInputChange(
        value,
        name,
        setCommentError,
        setCommentLengthError,
        handleInputReviewInput
      );
    }
  };

  return (
    <>
      <Dialog
        className="review-modal"
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: "22.125rem",
            zIndex: 1300,
            borderRadius: "0.625rem",
            margin: 0,
            padding: 0,
          },
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <DialogTitle className="review-dialog-title">
              {getDialogTitle(isEditMode, isReopenTextField)}
            </DialogTitle>
            <DialogContent className="review-modal-content">
              {(isEditMode || isReviewMode) && (
                <Box>
                  <TextField
                    className="review-dialog-score"
                    autoFocus
                    id="score"
                    shrink={false}
                    size="small"
                    label="Score"
                    type="text"
                    fullWidth
                    margin="none"
                    value={reviewDetails?.score}
                    variant="outlined"
                    onChange={(e) => handleInputChange(e.target.value, "score")}
                    color="secondary"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ "& .MuiTypography-root": { color: "#948B9E" } }}
                        >
                          {`/${reviewDetails?.maxExerciseScore}`}
                        </InputAdornment>
                      ),
                    }}
                    error={!!scoreError}
                    helperText={scoreError}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        input: {
                          "&::placeholder": {
                            opacity: 1,
                            color: "#2E1C41",
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}

              <TextField
                multiline
                autoFocus={isReopenTextField ? true : false}
                minRows={4}
                id="comment"
                shrink={false}
                size="small"
                label="Comment"
                type="text"
                fullWidth
                margin="none"
                variant="outlined"
                value={reviewDetails?.comment}
                onChange={(e) => handleInputChange(e?.target?.value, "comment")}
                color="secondary"
                error={commentError || commentLengthError}
                helperText={
                  commentError
                    ? "Comment cannot be empty"
                    : commentLengthError
                    ? "Maximum 500 characters allowed"
                    : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    textarea: {
                      "&::placeholder": {
                        opacity: 1,
                        color: "#2E1C41",
                      },
                    },
                  },
                }}
              />
            </DialogContent>
            <DialogActions className="review-dialog-action">
              <Button
                variant="outlined"
                disabled={loader}
                color="secondary"
                onClick={onClose}
                className="addTagButtonStyle"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                disableElevation
              >
                <Typography variant="outlineBtnLabel">Cancel</Typography>
              </Button>
              <Button
                fullWidth
                disabled={loader || (isEditMode && !exerciseDetailsChanged)}
                color="secondary"
                variant="contained"
                onClick={onSubmit}
                className="addTagButtonStyle"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                disableElevation
              >
                {loader ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Typography variant="outlineBtnLabel">Submit</Typography>
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ReviewModal;
